import { Auth0Context, Auth0Provider } from '@auth0/auth0-react'
import { ErrorBoundary } from '@sentry/gatsby'
import React from 'react'

import { isStorageDisabledError } from '../utils/storage'
import { Auth0ContextOverrideProvider } from './Auth0ContextOverrideProvider'
import { RecoveryReason, RenderLoopRecoveryFallback } from './RenderLoopRecoveryFallback'

const stubFor =
  ({ message }: { message: string }) =>
  (): never => {
    throw new Error(`Auth0 context not available: ${message}`)
  }

const asyncStubFor =
  ({ message }: { message: string }) =>
  <T,>(): Promise<T> =>
    Promise.reject(new Error(`Auth0 context not available: ${message}`))

const Auth0StubProvider: React.FC<{ error: Error; message: string }> = ({
  children,
  error,
  message,
}) => {
  const stub = stubFor({ message })
  const asyncStub = asyncStubFor({ message })

  return (
    <Auth0Context.Provider
      value={{
        error,
        isAuthenticated: false,
        isLoading: false,
        buildAuthorizeUrl: asyncStub,
        buildLogoutUrl: stub,
        getAccessTokenSilently: asyncStub,
        getAccessTokenWithPopup: asyncStub,
        getIdTokenClaims: asyncStub,
        loginWithRedirect: asyncStub,
        loginWithPopup: asyncStub,
        logout: stub,
        handleRedirectCallback: asyncStub,
      }}
    >
      {children}
    </Auth0Context.Provider>
  )
}

/**
 * Detect React's "Maximum update depth exceeded" error (an unbounded synchronous render/setState
 * loop). We must not respond to this by re-mounting the same subtree, since doing so re-enters the loop
 * and produces a storm of error-boundary catches plus a frozen page (see Sentry DFH-CORP-SITES-JSG).
 *
 * The development build spells the invariant out; production React 17 minifies it to error #185
 * (https://reactjs.org/docs/error-decoder.html?invariant=185). Match both forms, but narrowly: the
 * `185` boundaries avoid matching an unrelated invariant like 1850, so other React errors keep the
 * generic fallback.
 */
export const isMaxUpdateDepthError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false
  }
  const { message } = error
  return (
    /Maximum update depth exceeded/.test(message) ||
    /Minified React error #185(?!\d)/.test(message) ||
    /invariant=185(?!\d)/.test(message)
  )
}

/**
 * A DOM operation React expected to succeed found the tree changed under it. In practice that is a
 * browser translator (Chrome, Edge) having moved text nodes React owns into its own `<font>`
 * wrappers (Sentry DFH-CORP-SITES-E1V). A re-mount often gets past a one-off swap, but a reload
 * does not, since the browser translates the page again.
 */
export const isForeignDomMutationError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'NotFoundError'

// Shared by both fallbacks, so a render loop that starts after the re-mount is still tagged as one.
const recoveryReasonFor = (error: unknown): RecoveryReason =>
  isMaxUpdateDepthError(error)
    ? 'render-loop'
    : isForeignDomMutationError(error)
      ? 'dom-mutation'
      : 'remount-failed'

export type Auth0WithFallbackProviderProps = React.ComponentProps<typeof Auth0Provider>

/**
 * Allow site to render if an error occurs initializing Auth0 provider.
 *
 * Auth0Provider attempts to access session storage directly (for transaction support),
 * and this triggers errors in all major browsers when local storage/cookes are blocked.
 * This fallback solution allows the site to render despite impaired functionality.
 */
const Auth0WithFallbackProvider: React.FC<Auth0WithFallbackProviderProps> = ({
  children,
  ...otherProps
}) => {
  const app = (
    <Auth0Provider {...otherProps}>
      <Auth0ContextOverrideProvider>{children}</Auth0ContextOverrideProvider>
    </Auth0Provider>
  )

  return (
    <ErrorBoundary
      fallback={({ error, eventId }) =>
        // stub Auth0 context if attempt to access session storage triggered error boundary
        isStorageDisabledError(error) ? (
          <Auth0StubProvider error={error} message="storage disabled">
            {children}
          </Auth0StubProvider>
        ) : isMaxUpdateDepthError(error) ? (
          // A render loop: re-mounting the same tree would re-enter the loop and storm the boundary
          // (Sentry DFH-CORP-SITES-JSG). Show a recovery UI instead so the page can't freeze.
          <RenderLoopRecoveryFallback eventId={eventId} reason={recoveryReasonFor(error)} />
        ) : (
          // Re-mount once for anything else. Nothing sits above this boundary, so without the inner
          // one a second failure in the re-mounted app would unmount the React root and leave a
          // blank page.
          <ErrorBoundary
            fallback={({ error: remountError, eventId: remountEventId }) => (
              <RenderLoopRecoveryFallback
                eventId={remountEventId}
                reason={recoveryReasonFor(remountError)}
              />
            )}
          >
            {app}
          </ErrorBoundary>
        )
      }
    >
      {app}
    </ErrorBoundary>
  )
}

export default Auth0WithFallbackProvider
